import { IFilterFunction, IOnInit, IPromise, IQService, IScope } from "angular";
import {
  IFieldArray,
  IFieldConfigurationObject,
  IFieldGroup,
  ITemplateOptions,
  ITemplateScope,
} from "AngularFormly";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../records/forms/formly/fieldConfigurationHelpers";
import { RecordRepeaterController } from "../../../../records/forms/formly/types/repeater/controller";
import { FormViewController } from "../../../../records/forms/formView/controller";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../records/server/serverUtility";
import fieldHelpers, {
  ReadonlyFieldFormatter,
} from "../../../../ui-formly/fieldHelpers";
import { vsObjectName as iViewVersionName } from "../../config/iViewVersion";
import { vsObjectName as machineAreaName } from "../../config/machineArea";
import { vsObjectName as machineBankName } from "../../config/machineBank";
import { vsObjectName as machineDenominationName } from "../../config/machineDenomination";
import { vsObjectName as machineGameCategoryName } from "../../config/machineGameCategory";
import { vsObjectName as machineManufacturerName } from "../../config/machineManufacturer";
import { vsObjectName as modelName } from "../../config/machineModel";
import { vsObjectName as machineModelTypeName } from "../../config/machineModelType";
import { vsObjectName as machineThemeName } from "../../config/machineTheme";
import { vsObjectName as machineZoneName } from "../../config/machineZone";
import { vsObjectName as progressiveTypeName } from "../../config/progressiveType";
import {
  MachineGateway,
  MachineGameCategory,
  MachineModel,
  MachineModelType,
} from "../../models/machine";
import {
  MachineAndConfiguration,
  MachineManufacturer,
} from "../../../shared/models/machine";
import {
  IViewVersion,
  MachineArea,
  MachineBank,
  MachineConfigurationGateway,
  MachineGameConfigurationGateway,
  MachineZone,
} from "../../models/machineConfiguration";
import { Property } from "../../../../property/model/Property";
import {
  MachineDenomination,
  MachineTheme,
} from "../../../shared/models/machineConfiguration";
import { ProgressiveDefinition } from "../../models/progressiveDefinition";
import { ensureEnabledQueryBuilder } from "../../../../records/forms/formly/types/recordSelectAsync/utils";
import { vsTypeName as progressiveSelectTypeName } from "../../views/machines/detail/progressiveConfiguration/edit/progressiveDefinitionSelect";
import {
  LookupModalService,
  vsServiceName as LookupModalServiceName,
} from "../../../../records/lookup/lookupModal";
import { LookupManagementService } from "../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../../shared/lookups";
import { template } from "lodash";

const ThemeSymbol = Symbol("theme");
const DenominationSymbol = Symbol("denomination");

export interface IMachineEditFormModel {
  Machine: MachineGateway;
  Configuration: MachineConfigurationGateway;
}

export class MachinesEditFormController implements IOnInit {
  static $inject = [
    FieldConfigurationHelperServiceName,
    LookupModalServiceName,
    ServerUtilityName,
    "$q",
    MachineLookupManagementServiceName,
    "currencyFilter",
    "percentFilter",
  ];

  formFields: IFieldArray = [];
  additionalInfo: {
    MachineModelTypeId?: number;
    MachineAreaId?: number;
    MachineZoneId?: number;
    MachineManufacturerId?: number;
  } = {};

  /* bindings */
  model!: IMachineEditFormModel;
  machineFieldsEditable: boolean = true;
  onSubmit!: (
    FormViewController: FormViewController,
    model: IMachineEditFormModel
  ) => Promise<void>;
  onCancel!: (
    FormViewController: FormViewController,
    model: IMachineEditFormModel
  ) => Promise<void>;
  progressiveDefinition!: ProgressiveDefinition | undefined;
  /* */

  constructor(
    private readonly fieldConfigurationHelperService: FieldConfigurationHelperService,
    private readonly lookupModalService: LookupModalService,
    private readonly serverUtility: ServerUtility,
    private readonly $q: IQService,
    private readonly machineLookupManagementService: LookupManagementService,
    private readonly currencyFilter: IFilterFunction,
    private readonly percentFilter: IFilterFunction
  ) {}

  $onInit(): void {
    const softwares = (this.model.Configuration.Softwares ??= []);
    for (let i = softwares.length; i < 3; i++) {
      softwares.push({
        Id: 0,
      });
    }

    this.additionalInfo = {
      MachineModelTypeId: this.getMachineModel()?.MachineModelTypeId,
      MachineAreaId: this.getMachineZone()?.MachineAreaId,
      MachineZoneId: this.getMachineBank()?.MachineZoneId,
      MachineManufacturerId: this.getMachineModelType()?.MachineManufacturerId,
    };

    this.formFields = this.getFormFields(this.machineFieldsEditable);
  }

  private getGameConfigFields() {
    return [
      {
        className: "row",
        fieldGroup: [
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineThemeId",
            name: "MachineThemeId",
            label: "Theme",
            required: true,
            disableDefaultQueryCaching: true,
            getDefaultItem: (model) => model[ThemeSymbol] ?? model.MachineTheme,
            queryUrl: "/api/machines/MachineTheme",
            queryBuilder: ensureEnabledQueryBuilder(),
            className: "col-xs-6",
            onSelect: (selectedItem, model: any) => {
              const gameConfig = <MachineGameConfigurationGateway>model;
              const theme = <MachineTheme>selectedItem;
              gameConfig[ThemeSymbol] = model.MachineTheme = theme;
              return;
            },
            addonRight: this.getDropdownAddAddon(
              machineThemeName,
              (value, scope) => {
                const s = <any>scope;
                s.model.MachineThemeId = value.Id;
              },
              (options, model, scope) => {
                return this.$q.when({
                  Disabled: false,
                });
              }
            ),
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineDenominationId",
            name: "MachineDenominationId",
            label: "Denomination",
            required: true,
            getDefaultItem: (model) =>
              model[DenominationSymbol] ?? model.MachineDenomination,
            queryUrl: "/api/machines/MachineDenomination",
            queryBuilder: ensureEnabledQueryBuilder(),
            disableDefaultQueryCaching: true,
            className: "col-xs-3",
            onSelect: (selectedItem, model: any) => {
              const gameConfig = <MachineGameConfigurationGateway>model;
              const denomination = <MachineDenomination>selectedItem;
              gameConfig[DenominationSymbol] = model.MachineDenomination = denomination;
            },
            addonRight: this.getDropdownAddAddon(
              machineDenominationName,
              (value, scope) => {
                const s = <any>scope;
                s.model.MachineDenominationId = value.Id;
              },
              (options, model, scope) => {
                return this.$q.when({
                  Disabled: false,
                });
              }
            ),
          }),
          fieldHelpers.simpleStringField({
            key: "PaytableId",
            name: "PaytableId",
            label: "Paytable ID",
            className: "col-xs-3",
          }),
        ],
      },
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleStringField({
            key: "GameProgramNumber",
            name: "GameProgramNumber",
            label: "Game Program Number",
          }),
          fieldHelpers.simpleStringField({
            key: "GameProgramVersion",
            name: "GameProgramVersion",
            label: "Game Program Version",
          }),
          fieldHelpers.simplePercentageField({
            key: "MinPaybackPercentage",
            name: "MinPaybackPercentage",
            label: "Min Payback %",
            min: 0,
            step: 0.0001,
            expressionProperties: {
              "templateOptions.max": (
                $viewValue: any,
                $modelValue: any,
                scope: ITemplateScope
              ) => {
                return (<any>(<unknown>scope)).model.MaxPaybackPercentage;
              },
            },
          }),
          fieldHelpers.simplePercentageField({
            key: "MaxPaybackPercentage",
            name: "MaxPaybackPercentage",
            label: "Max Payback %",
            min: 0,
            step: 0.0001,
          }),
          fieldHelpers.simplePercentageField({
            key: "MaxPaybackWithContribution",
            name: "MaxPaybackWithContribution",
            label: "Max Packback with Contribution",
            min: 0,
            step: 0.0001,
          }),
          fieldHelpers.simplePercentageField({
            key: "GameHoldPercentage",
            name: "GameHoldPercentage",
            label: "Hold %",
            min: 0,
            step: 0.0001,
          }),
          fieldHelpers.simpleNumberField({
            key: "MinCreditsBet",
            name: "MinCreditsBet",
            label: "Min Bet (credits)",
            min: 0,
            step: 1,
          }),
          fieldHelpers.simpleNumberField({
            key: "MaxCreditBet",
            name: "MaxCreditBet",
            label: "Max Bet (credits)",
            min: 0,
            step: 1,
          }),
          fieldHelpers.simpleNumberField({
            key: "TopCreditsAward",
            name: "TopCreditsAward",
            label: "Top Award (credits)",
            min: 0,
            step: 1,
          }),
          fieldHelpers.simpleCurrencyField({
            key: "TopDollarAward",
            name: "TopDollarAward",
            label: "Top Award (Dollar)",
            min: 0,
            step: 0.01,
          }),
          fieldHelpers.simpleStringField({
            key: "Paylines",
            name: "Paylines",
            label: "# of Paylines",
          }),
          fieldHelpers.simpleStringField({
            key: "Reels",
            name: "Reels",
            label: "# of Reels",
          }),
          fieldHelpers.simplePercentageField({
            key: "Volatility",
            name: "Volatility",
            label: "Volatility",
            step: 0.0001,
          }),
          {
            key: "copyDelete",
            type: "recordRepeaterCopyDelete",
            templateOptions: {
              label: " ",
              allowMove: false,
              allowEditComplete: true,
            },
          },
        ],
        4
      ),
    ];
  }

  private getFormFields(machineFieldsEditable: boolean): IFieldArray {
    const that = this;

    const gameConfigFields = this.getGameConfigFields();
    const readonlyGameConfigFields =
      this.getGameConfigReadonlyFields(gameConfigFields);

    return [
      fieldHelpers.headerField({
        headerText: "Cabinet Information",
        key: "cabHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.PropertyId",
            name: "Configuration.PropertyId",
            label: "Property",
            required: true,
            disableDefaultQueryCaching: true,
            getDefaultItem: () => {
              return this.getProperty();
            },
            queryUrl: "/api/properties",
          }),
          fieldHelpers.simpleStringField({
            key: "Machine.SerialNumber",
            label: "Serial Number",
            name: "Machine.SerialNumber",
            required: true,
            expressionProperties: {
              "templateOptions.readonly": () => !machineFieldsEditable,
            },
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.SlotNumber",
            name: "Configuration.SlotNumber",
            label: "Asset Number",
            expressionProperties: {
              "templateOptions.required": () => that.isFloorStatus(),
            },
          }),
          {
            key: "Configuration.Status",
            name: "Configuration.Status",
            type: "select",
            templateOptions: {
              label: "Status",
              options: [
                {
                  label: "Floor",
                  value: "Floor",
                },
                {
                  label: "Warehoused",
                  value: "Warehoused",
                },
                {
                  label: "Decommissioned",
                  value: "Decommissioned",
                },
              ],
              required: true,
              labelProp: "label",
              valueProp: "value",
            },
          },
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineAreaId",
            name: "MachineAreaId",
            label: "Area",
            model: this.additionalInfo,
            disableDefaultQueryCaching: true,
            getDefaultItem: () => this.getMachineArea(),
            queryUrl: "/api/machines/MachineArea",
            queryBuilder: ensureEnabledQueryBuilder((search) => {
              const propertyId = this.model.Configuration.PropertyId ?? 0;
              return {
                NameContains: search,
                PropertyId: propertyId,
              };
            }),
            watcher: [
              {
                expression: () => {
                  return that.model.Configuration.PropertyId;
                },
                listener: (_, newValue, oldValue) => {
                  if (newValue !== oldValue) {
                    that.additionalInfo.MachineAreaId = undefined;
                  }
                },
              },
              {
                expression: () => that.isFloorStatus(),
                listener: (_, isFloorStatus) => {
                  if (!isFloorStatus) {
                    that.additionalInfo.MachineAreaId = undefined;
                  }
                },
              },
            ],
            hideExpression: () => !that.isFloorStatus(),
            expressionProperties: {
              "templateOptions.readonly": () => {
                return !(
                  that.model.Configuration.PropertyId && that.isFloorStatus()
                );
              },
              "templateOptions.required": () => that.isFloorStatus(),
            },
            addonRight: this.getDropdownAddAddon(
              machineAreaName,
              (area: any) => (this.additionalInfo.MachineAreaId = area.Id),
              (options: ITemplateOptions, model: any, scope: IScope) => {
                const propertyId = that.model.Configuration.PropertyId;
                if (!propertyId) return this.$q.when({});

                return this.serverUtility
                  .querySingle<Property>({
                    query: { Id: propertyId },
                    url: "/api/properties",
                  })
                  .then((property) => {
                    if (property === undefined) {
                      return undefined;
                    }
                    return {
                      PropertyId: property.Id,
                      PropertyName: property.Name,
                      Disabled: false,
                    };
                  });
              }
            ),
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineZoneId",
            name: "MachineZoneId",
            label: "Zone",
            model: this.additionalInfo,
            getDefaultItem: () => this.getMachineZone(),
            disableDefaultQueryCaching: true,
            queryUrl: "/api/machines/MachineZone",
            queryBuilder: ensureEnabledQueryBuilder((search) => {
              const machineAreaId = this.additionalInfo.MachineAreaId ?? 0;
              return {
                NameContains: search,
                MachineAreaId: machineAreaId,
              };
            }),
            watcher: [
              {
                expression: () => that.additionalInfo.MachineAreaId,
                listener: (_, newValue, oldValue) => {
                  if (newValue !== oldValue) {
                    that.additionalInfo.MachineZoneId = undefined;
                  }
                },
              },
              {
                expression: () => that.isFloorStatus(),
                listener: (_, isFloorStatus) => {
                  if (!isFloorStatus) {
                    that.additionalInfo.MachineZoneId = undefined;
                  }
                },
              },
            ],
            hideExpression: () => !that.isFloorStatus(),
            expressionProperties: {
              "templateOptions.readonly": () => {
                return !(
                  that.additionalInfo.MachineAreaId && that.isFloorStatus()
                );
              },
              "templateOptions.required": () => that.isFloorStatus(),
            },
            addonRight: this.getDropdownAddAddon(
              machineZoneName,
              (value) => (that.additionalInfo.MachineZoneId = value.Id),
              (options, model, scope) => {
                const machineAreaId = that.additionalInfo.MachineAreaId;
                if (!machineAreaId) return this.$q.when({});

                return this.serverUtility
                  .querySingle<MachineArea>({
                    url: "/api/machines/MachineArea",
                    query: { Id: machineAreaId },
                  })
                  .then((machineArea) => {
                    if (machineArea === undefined) return undefined;

                    return {
                      PropertyId: machineArea.PropertyId,
                      PropertyName: machineArea.PropertyName,
                      MachineAreaId: machineArea.Id,
                      MachineAreaName: machineArea.Name,
                      Disabled: false,
                    };
                  });
              }
            ),
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.MachineBankId",
            name: "Configuration.MachineBankId",
            label: "Bank",
            getDefaultItem: () => that.getMachineBank(),
            queryBuilder: ensureEnabledQueryBuilder((search) => {
              const machineZoneId = that.additionalInfo.MachineZoneId ?? 0;
              return {
                NameContains: search,
                MachineZoneId: machineZoneId,
              };
            }),
            queryUrl: "/api/machines/MachineBank",
            disableDefaultQueryCaching: true,
            hideExpression: () => !that.isFloorStatus(),
            expressionProperties: {
              "templateOptions.readonly": () => {
                return !(
                  that.additionalInfo.MachineZoneId && that.isFloorStatus()
                );
              },
              "templateOptions.required": () => that.isFloorStatus(),
            },
            watcher: [
              {
                expression: () => that.additionalInfo.MachineZoneId,
                listener: (_, newValue, oldValue) => {
                  if (newValue !== oldValue) {
                    that.model.Configuration.MachineBankId = undefined;
                  }
                },
              },
              {
                expression: () => that.isFloorStatus(),
                listener: (_, isFloorStatus) => {
                  if (!isFloorStatus) {
                    that.model.Configuration.MachineBankId = undefined;
                  }
                },
              },
            ],
            addonRight: this.getDropdownAddAddon(
              machineBankName,
              (value) => (that.model.Configuration.MachineBankId = value.Id),
              (options, model, scope) => {
                const machineZoneId = that.additionalInfo.MachineZoneId;
                if (!machineZoneId) return this.$q.when({});

                return this.serverUtility
                  .querySingle<MachineZone>({
                    url: "/api/machines/MachineZone",
                    query: { Id: machineZoneId },
                  })
                  .then((machineZone) => {
                    if (machineZone === undefined) return undefined;
                    return {
                      PropertyId: machineZone.PropertyId,
                      PropertyName: machineZone.PropertyName,
                      MachineAreaId: machineZone.MachineAreaId,
                      MachineAreaName: machineZone.MachineAreaName,
                      MachineZoneId: machineZone.Id,
                      MachineZoneName: machineZone.Name,
                      Disabled: false,
                    };
                  });
              }
            ),
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.Position",
            name: "Configuration.Position",
            label: "Position",
            minlength: 2,
            maxlength: 2,
            hideExpression: () => !that.isFloorStatus(),
            expressionProperties: {
              "templateOptions.required": () => that.isFloorStatus(),
              "templateOptions.readonly": () => !that.isFloorStatus(),
            },
            watcher: {
              expression: () => that.isFloorStatus(),
              listener: (_, isFloorStatus) => {
                if (!isFloorStatus) {
                  that.model.Configuration.Position = undefined;
                }
              },
            },
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.WarehouseLocation",
            name: "Configuration.WarehouseLocation",
            label: "Location",
            hideExpression: () => !that.isWarehouseStatus(),
            watcher: {
              expression: () => that.isWarehouseStatus(),
              listener: (_, isWarehouseStatus) => {
                if (!isWarehouseStatus) {
                  that.model.Configuration.WarehouseLocation = undefined;
                }
              },
            },
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineManufacturerId",
            name: "MachineManufacturerId",
            label: "Manufacturer",
            required: true,
            expressionProperties: {
              "templateOptions.readonly": () => !machineFieldsEditable,
            },
            disableDefaultQueryCaching: true,
            model: this.additionalInfo,
            queryUrl: "/api/machines/MachineManufacturer",
            queryBuilder: ensureEnabledQueryBuilder(),
            getDefaultItem: () => that.getMachineManufacturer(),
            addonRight: this.getDropdownAddAddon(
              machineManufacturerName,
              (value) => (that.additionalInfo.MachineManufacturerId = value.Id),
              (options, model, scope) => {
                return this.$q.when({
                  Disabled: false,
                });
              }
            ),
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "MachineModelTypeId",
            name: "MachineModelTypeId",
            label: "Model Type",
            required: true,
            model: this.additionalInfo,
            queryUrl: "/api/machines/MachineModelType",
            getDefaultItem: () => that.getMachineModelType(),
            disableDefaultQueryCaching: true,
            watcher: {
              expression: () => that.additionalInfo.MachineManufacturerId,
              listener: (_, newValue, oldValue) => {
                if (newValue !== oldValue) {
                  that.additionalInfo.MachineModelTypeId = undefined;
                }
              },
            },
            queryBuilder: ensureEnabledQueryBuilder((search) => {
              const machineManufacturerId =
                that.additionalInfo.MachineManufacturerId ?? 0;
              return {
                NameContains: search,
                MachineManufacturerId: machineManufacturerId,
              };
            }),
            expressionProperties: {
              "templateOptions.readonly": () =>
                !that.additionalInfo.MachineManufacturerId ||
                !machineFieldsEditable,
            },
            addonRight: this.getDropdownAddAddon(
              machineModelTypeName,
              (value) => (that.additionalInfo.MachineModelTypeId = value.Id),
              (options, model, scope) => {
                const machineManufactuterId =
                  that.additionalInfo.MachineManufacturerId;
                if (!machineManufactuterId) return this.$q.when({});

                return this.serverUtility
                  .querySingle<MachineManufacturer>({
                    url: "/api/machines/MachineManufacturer",
                    query: { Id: machineManufactuterId },
                  })
                  .then((machineManufacturer) => {
                    if (machineManufacturer === undefined) return undefined;
                    return {
                      MachineManufacturerId: machineManufacturer.Id,
                      MachineManufacturerName: machineManufacturer.Name,
                      Disabled: false,
                    };
                  });
              }
            ),
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "Machine.MachineModelId",
            name: "Machine.MachineModelId",
            label: "Model",
            required: true,
            queryUrl: "/api/machines/MachineModel",
            getDefaultItem: () => that.getMachineModel(),
            queryBuilder: ensureEnabledQueryBuilder((search) => {
              const machineModelTypeId =
                that.additionalInfo.MachineModelTypeId ?? 0;
              return {
                NameContains: search,
                MachineModelTypeId: machineModelTypeId,
              };
            }),
            disableDefaultQueryCaching: true,
            watcher: {
              expression: () => that.additionalInfo.MachineModelTypeId,
              listener: (_, newValue, oldValue) => {
                if (newValue !== oldValue) {
                  that.model.Machine.MachineModelId = undefined;
                }
              },
            },
            expressionProperties: {
              "templateOptions.readonly": () =>
                !that.additionalInfo.MachineModelTypeId ||
                !machineFieldsEditable,
            },
            addonRight: this.getDropdownAddAddon(
              modelName,
              (value) => (that.model.Machine.MachineModelId = value.Id),
              (options, model, scope) => {
                const machineModelTypeId =
                  that.additionalInfo.MachineModelTypeId;
                if (!machineModelTypeId) return this.$q.when({});

                return this.serverUtility
                  .querySingle<MachineModelType>({
                    url: "/api/machines/MachineModelType",
                    query: { Id: machineModelTypeId },
                  })
                  .then((machineModelType) => {
                    if (machineModelType === undefined) return undefined;
                    return {
                      MachineManufacturerId:
                        machineModelType.MachineManufacturerId,
                      MachineManufacturerName:
                        machineModelType.MachineManufacturerName,
                      MachineModelTypeId: machineModelType.Id,
                      MachineModelTypeName: machineModelType.Name,
                      Disabled: false,
                    };
                  });
              }
            ),
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.Topper",
            name: "Configuration.Topper",
            label: "Topper",
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.MachineGameCategoryId",
            name: "Configuration.MachineGameCategoryId",
            label: "Game Category",
            required: true,
            queryUrl: "/api/machines/MachineGameCategory",
            queryBuilder: ensureEnabledQueryBuilder(),
            disableDefaultQueryCaching: true,
            serverItemMapper(model) {
              return {
                ...model,
              };
            },
            onSelect(selectedItem, model) {
              const m = <IMachineEditFormModel>model;
              m.Configuration.MachineGameCategory = selectedItem;
            },
            getDefaultItem: () =>
              <MachineGameCategory>that.model.Configuration.MachineGameCategory,
            addonRight: this.getDropdownAddAddon(
              machineGameCategoryName,
              (value) =>
                (that.model.Configuration.MachineGameCategoryId = value.Id),
              (options, model, scope) => {
                return this.$q.when({
                  Disabled: false,
                });
              }
            ),
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.GameType",
            name: "Configuration.GameType",
            label: "Game Type override",
            required: false,
            expressionProperties: {
              "templateOptions.helpText":
                "'Default: ' + model.Configuration.MachineGameCategory.GameType",
            },
          }),
          fieldHelpers.simpleBoolField({
            key: "Configuration.Roulette",
            name: "Configuration.Roulette",
            label: "Roulette (Y/N)",
            defaultValue: false,
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.RouletteId",
            name: "Configuration.RouletteId",
            label: "Roulette ID#",
            expressionProperties: {
              "templateOptions.required": () =>
                that.model.Configuration.Roulette,
              "templateOptions.readonly": () =>
                !that.model.Configuration.Roulette,
            },
            watcher: {
              expression: () => that.model.Configuration.Roulette,
              listener: (_, newValue, __) => {
                if (!newValue) {
                  that.model.Configuration.RouletteId = undefined;
                }
              },
            },
          }),
          fieldHelpers.simpleCurrencyField({
            key: "Configuration.JackpotLimit",
            name: "Configuration.JackpotLimit",
            label: "JP Limit",
            min: 0,
            step: 0.01,
          }),
          fieldHelpers.simpleCurrencyField({
            key: "Configuration.CreditLimit",
            name: "Configuration.CreditLimit",
            label: "Credit Limit",
            min: 0,
            step: 0.01,
          }),
          fieldHelpers.simpleCurrencyField({
            key: "Configuration.PrinterLimit",
            name: "Configuration.PrinterLimit",
            label: "Printer Limit",
            min: 0,
            step: 0.01,
          }),
          fieldHelpers.simpleCurrencyField({
            key: "Configuration.BillLimit",
            name: "Configuration.BillLimit",
            label: "Bill Limit",
            min: 0,
            step: 0.01,
          }),
          fieldHelpers.simpleDateField({
            key: "Machine.ManufacturersDate",
            name: "Machine.ManufacturersDate",
            label: "Manufacturer's Date",
            expressionProperties: {
              "templateOptions.disabled": () => !machineFieldsEditable,
            },
          }),
          fieldHelpers.simpleDateField({
            key: "Configuration.InServiceDate",
            name: "Configuration.InServiceDate",
            label: "In Service Date",
            expressionProperties: {
              "templateOptions.required": () => that.isFloorStatus(),
            },
          }),
          fieldHelpers.simpleDateField({
            key: "Configuration.Date",
            name: "Configuration.Date",
            label: "Project Date",
            required: true,
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.AccountingDenominationId",
            name: "Configuration.AccountingDenominationId",
            label: "Accounting Denomination",
            required: true,
            disableDefaultQueryCaching: true,
            getDefaultItem: () =>
              <MachineDenomination>(
                that.model.Configuration.AccountingDenomination
              ),
            queryUrl: "/api/machines/MachineDenomination",
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              machineDenominationName,
              (value) =>
                (that.model.Configuration.AccountingDenominationId = value.Id),
              (options, model, scope) => {
                return this.$q.when({
                  Disabled: false,
                });
              }
            ),
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.MeterDelta",
            name: "Configuration.MeterDelta",
            label: "Meter Delta",
          }),
          fieldHelpers.simpleBoolField({
            key: "Configuration.TournamentEnabled",
            name: "Configuration.TournamentEnabled",
            label: "Tournament Enabled (Y/N)",
            defaultValue: false,
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.ProjectNumber",
            name: "Configuration.ProjectNumber",
            label: "Project Number",
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Lease Information",
        key: "leaseHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          {
            key: "Configuration.Ownership",
            name: "Configuration.Ownership",
            type: "select",
            templateOptions: {
              label: "Ownership",
              options: [
                {
                  label: "Owned",
                  value: "Owned",
                },
                {
                  label: "Leased",
                  value: "Leased",
                },
                {
                  label: "Leased/Owned",
                  value: "LeasedOwned",
                },
              ],
              required: true,
              labelProp: "label",
              valueProp: "value",
            },
          },
          fieldHelpers.simpleBoolField({
            key: "Configuration.LeasedSoftware",
            name: "Configuration.LeasedSoftware",
            label: "Leased Software",
            defaultValue: false,
            watcher: {
              expression: () => that.model.Configuration.Ownership,
              listener: (_, ownership) => {
                if (ownership === "Leased") {
                  that.model.Configuration.LeasedSoftware = true;
                }
              },
            },
            expressionProperties: {
              "templateOptions.readonly": (
                $viewValue: any,
                $modelValue: any,
                scope: ITemplateScope
              ) => {
                return that.model.Configuration.Ownership === "Leased";
              },
            },
          }),
          fieldHelpers.simpleCurrencyField({
            key: "Configuration.DailyFee",
            name: "Configuration.DailyFee",
            label: "Daily Fee",
            step: 0.01,
            min: 0,
          }),
          fieldHelpers.simplePercentageField({
            key: "Configuration.CoinInPercentage",
            name: "Configuration.CoinInPercentage",
            label: "Percentage of Coin In",
            min: 0,
            max: 100,
            step: 0.0001,
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.ContractNumber",
            name: "Configuration.ContractNumber",
            label: "Contract Number",
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.MinDaysNotification",
            name: "Configuration.MinDaysNotification",
            label: "Min Days Notification",
            step: 1,
            min: 0,
          }),
          fieldHelpers.simpleDateField({
            key: "Configuration.ContractExpiryDate",
            name: "Configuration.ContractExpiryDate",
            label: "Contract Expiry",
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Game Information",
        key: "gameHeader",
      }),
      this.fieldConfigurationHelperService.editRepeaterField({
        key: "Configuration.GamesConfig",
        name: "Configuration.GamesConfig",
        allowAdd: true,
        allowAddBottom: false,
        displayNameSingular: "Game",
        displayNamePlural: "Games",
        showHeader: false,
        editFields: gameConfigFields,
        repeaterFields: readonlyGameConfigFields,
      }),
      ...fieldHelpers.columnLayout(
        [
          {
            id: "Configuration.GameTheme",
            key: "Configuration.GameTheme",
            templateOptions: {
              label: "Game Theme",
              required: true,
            },
            type: "input",
            hideExpression: () => !that.isMultiTheme(),
            watcher: {
              expression: () => that.isMultiTheme(),
              listener: (_, nowMultiTheme) => {
                if (!nowMultiTheme) {
                  that.model.Configuration.GameTheme = null;
                }
              },
            },
          },
        ],
        2
      ),
      fieldHelpers.headerField({
        headerText: "Progressive Information",
        key: "ProgressiveHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          {
            key: "Configuration.ProgressiveDefinitionId",
            name: "Configuration.ProgressiveDefinitionId",
            type: progressiveSelectTypeName,
            templateOptions: {
              label: "Progressive",
            },
            data: {
              initialProgressiveDefinition: this.progressiveDefinition,
            },
          },
          fieldHelpers.simpleStringField({
            key: "Configuration.LinkId",
            label: "Link ID",
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleStringField({
            key: "Configuration.ProgressiveControllerVersion",
            label: "Prog Controller Version",
          }),
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.ProgressiveTypeId",
            label: "Progressive Type",
            required: true,
            getDefaultItem: (
              model: MachineAndConfiguration<
                MachineGateway,
                MachineConfigurationGateway
              >
            ) => {
              return model.Configuration.ProgressiveType;
            },
            queryUrl: "/api/machines/progressives/ProgressiveType",
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              progressiveTypeName,
              (value, scope) => {
                that.model.Configuration.ProgressiveTypeId = value.Id;
              },
              (options, model, scope) =>
                this.$q.when({
                  Disabled: false,
                })
            ),
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.ProgressiveControllerSerialNumber",
            label: "Prog Controller Serial #",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.ProgressiveSoftware1",
            label: "Controller Software (1)",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.ProgressiveSoftware2",
            label: "Controller Software (2)",
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Game Software",
        key: "GameSoftwareHeader",
      }),
      this.fieldConfigurationHelperService.repeaterField({
        allowAdd: true,
        allowAddBottom: true,
        displayNameSingular: "Game Software",
        displayNamePlural: "Game Software",
        showHeader: false,
        key: "Configuration.Softwares",
        name: "Configuration.Softwares",
        repeaterFields: [
          {
            template: `<div class="form-control-static">{{getProgramNumber(model, $index)}}</div>`,
            controller: ProgramNumberFieldController,
            className: "col-xs-3",
            templateOptions: {
              label: "Program Number",
            },
            wrapper: ["bootstrapLabel", "bootstrapHasError"],
          },
          fieldHelpers.simpleStringField({
            key: "Software",
            name: "Software",
            label: "Software",
            className: "col-xs-4",
          }),
          fieldHelpers.simpleStringField({
            key: "Version",
            name: "Version",
            label: "Version",
            className: "col-xs-3",
          }),
          {
            key: "copyDelete",
            type: "recordRepeaterCopyDelete",
            templateOptions: {
              label: " ",
              allowMove: true,
            },
            expressionProperties: {
              "templateOptions.hideDelete": (
                $viewValue,
                $modelValue,
                scope
              ) => {
                const repeaterController: RecordRepeaterController = (<any>(
                  scope.formState
                )).getRepeaterController();
                const index = repeaterController.getRowIndex(scope.model);
                return index < 3;
              },
            },
            className: "col-xs-2",
          },
        ],
      }),
      fieldHelpers.headerField({
        key: "SDSSoftwareHeader",
        headerText: "SDS Software",
      }),
      ...fieldHelpers.columnLayout(
        [
          this.fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.IViewVersionId",
            name: "Configuration.IViewVersionId",
            label: "iView Version",
            getDefaultItem: () =>
              <IViewVersion>that.model.Configuration.IViewVersion,
            queryUrl: "/api/machines/iViewVersion",
            queryBuilder: ensureEnabledQueryBuilder(),
            disableDefaultQueryCaching: true,
            addonRight: this.getDropdownAddAddon(
              iViewVersionName,
              (value) => (that.model.Configuration.IViewVersionId = value.Id),
              (options, model, scope) => {
                return this.$q.when({
                  Disabled: false,
                });
              }
            ),
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.IViewContent",
            name: "Configuration.IViewContent",
            label: "iView Content",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.IViewOSVersion",
            name: "Configuration.IViewOSVersion",
            label: "iView OS Version",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.NXPFirmware",
            name: "Configuration.NXPFirmware",
            label: "NXP Firmware",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.GenesisFirmware",
            name: "Configuration.GenesisFirmware",
            label: "Genesis Firmware",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.GMUDOCID",
            name: "Configuration.GMUDOCID",
            label: "GMU DOC ID",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.ROMSIGSoftwareName",
            name: "Configuration.ROMSIGSoftwareName",
            label: "ROMSIG Software Name",
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        key: "AncillaryInformationHeader",
        headerText: "Ancillary Information",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleStringField({
            key: "Configuration.PrinterManufacturer",
            name: "Configuration.PrinterManufacturer",
            label: "Printer Manufacturer",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.PrinterModel",
            name: "Configuration.PrinterModel",
            label: "Printer Model",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.PrinterSerialNumber",
            name: "Configuration.PrinterSerialNumber",
            label: "Printer Serial #",
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleStringField({
            key: "Configuration.BillValidatorManufacturer",
            name: "Configuration.BillValidatorManufacturer",
            label: "Bill Validator Manufacturer",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.BillValidatorModel",
            name: "Configuration.BillValidatorModel",
            label: "Bill Validator Model",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.BVSerialNumber",
            name: "Configuration.BVSerialNumber",
            label: "BV Serial #",
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.BillValidatorCapacity",
            name: "Configuration.BillValidatorCapacity",
            label: "Bill Validator Capacity",
            step: 1,
            min: 0,
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        key: "SecurityDetailsHeader",
        headerText: "Security Details",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleNumberField({
            key: "Configuration.MainDoorMD5_8",
            name: "Configuration.MainDoorMD5_8",
            label: "Main Door (MD) 5/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.MainDoorMD1_1_8",
            name: "Configuration.MainDoorMD1_1_8",
            label: "Main Door (MD) 1 1/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.CPU5_8",
            name: "Configuration.CPU5_8",
            label: "CPU 5/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.CPU1_1_8",
            name: "Configuration.CPU1_1_8",
            label: "CPU 1 1/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.BellyDoorBVS5_8",
            name: "Configuration.BellyDoorBVS5_8",
            label: "Belly Door (BVS) 5/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.BellyDoorBVS1_1_8",
            name: "Configuration.BellyDoorBVS1_1_8",
            label: "Belly Door (BVS) 1 1/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.CashboxBVC5_8",
            name: "Configuration.CashboxBVC5_8",
            label: "Cashbox (BVC) 5/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.CashboxBVC1_1_8",
            name: "Configuration.CashboxBVC1_1_8",
            label: "Cashbox (BVC) 1 1/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.CashBoxDoorBVA5_8",
            name: "Configuration.CashBoxDoorBVA5_8",
            label: "Cash Box Door (BVA) 5/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.CashBoxDoorBVA1_1_8",
            name: "Configuration.CashBoxDoorBVA1_1_8",
            label: "Cash Box Door (BVA) 1 1/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.ElectricalBaseELE5_8",
            name: "Configuration.ElectricalBaseELE5_8",
            label: "Electrical/Base (ELE) 5/8",
            min: 0,
          }),
          fieldHelpers.simpleNumberField({
            key: "Configuration.ElectricalBaseELE1_1_8",
            name: "Configuration.ElectricalBaseELE1_1_8",
            label: "Electrical/Base (ELE) 1 1/8",
            min: 0,
          }),
        ],
        4
      ),
      {
        key: "Configuration.Files",
        name: "Configuration.Files",
        type: "recordFileUpload",
        templateOptions: {
          label: "Files",
          allowMultiple: true,
        },
      },
      fieldHelpers.headerField({
        key: "NotesHeader",
        headerText: "Notes",
      }),
      fieldHelpers.textAreaField({
        key: "Configuration.Notes",
        label: " ",
        name: "Notes",
        rows: 5,
      }),
    ];
  }

  submit(formViewController: FormViewController): IPromise<void> {
    return this.onSubmit(formViewController, this.model);
  }
  cancel(formViewController: FormViewController): Promise<void> {
    return this.onCancel(formViewController, this.model);
  }

  private getMachineBank(): MachineBank | undefined {
    return this.model?.Configuration?.MachineBank;
  }

  private getMachineArea(): MachineArea | undefined {
    return this.getMachineZone()?.MachineArea;
  }

  private getMachineZone(): MachineZone | undefined {
    return this.getMachineBank()?.MachineZone;
  }

  private getProperty(): Property | undefined {
    return this.model.Configuration.Property;
  }

  private getMachineModel(): MachineModel | undefined {
    return this.model?.Machine?.MachineModel;
  }

  private getMachineModelType(): MachineModelType | undefined {
    return this.getMachineModel()?.MachineModelType;
  }

  private getMachineManufacturer(): MachineManufacturer | undefined {
    return this.getMachineModelType()?.MachineManufacturer;
  }

  private isMultiTheme(): boolean {
    const gameConfigWithThemes =
      this.model.Configuration?.GamesConfig?.filter(
        (gc) => gc.MachineThemeId
      ) ?? [];
    const distinctThemes = gameConfigWithThemes.reduce((result, next) => {
      result.add(<number>next.MachineThemeId);
      return result;
    }, new Set<number>());
    return distinctThemes.size > 1;
  }

  private isFloorStatus(): boolean {
    return this.model.Configuration.Status === "Floor";
  }
  private isWarehouseStatus(): boolean {
    return this.model.Configuration.Status === "Warehoused";
  }
  private isDecomissionStatus(): boolean {
    return this.model.Configuration.Status === "Decommissioned";
  }

  private getGameConfigReadonlyFields(editFields: IFieldArray): IFieldArray {
    return editFields.reduce<IFieldArray>((result, next) => {
      result.push(this.getGameConfigReadonlyItem(next));
      return result;
    }, []);
  }

  private getGameConfigReadonlyItem(
    field: IFieldGroup | IFieldConfigurationObject
  ): any {
    const isFieldGroup = !!(<IFieldGroup>field).fieldGroup;
    return isFieldGroup
      ? this.getGameConfigReadonlyFieldGroup(<IFieldGroup>field)
      : this.getGameConfigReadonlyField(<IFieldConfigurationObject>field);
  }

  private static hasAddOnClass(
    templateOptions: ITemplateOptions,
    name: string
  ): boolean {
    const className = `fa ${name}`;
    return [
      templateOptions?.addonRight?.class,
      templateOptions?.addonLeft?.class,
    ].includes(className);
  }

  getGameConfigReadonlyField(
    field: IFieldConfigurationObject
  ): IFieldConfigurationObject {
    const fieldKey = field.key!.toString();
    const propertyKey = fieldKey.endsWith("Id")
      ? fieldKey.substring(0, fieldKey.length - 2)
      : undefined;
    const templateOptions: any = field.templateOptions ?? {};

    if (fieldKey === "copyDelete") {
      return {
        key: "copyDelete",
        type: "recordRepeaterCopyDelete",
        templateOptions: {
          label: " ",
          allowMove: true,
          allowEdit: true,
        },
      };
    }

    const formatters: ReadonlyFieldFormatter[] =
      field.type === "recordSelectAsync"
        ? [(_, scope) => scope.model?.[propertyKey!]?.["Name"]]
        : MachinesEditFormController.hasAddOnClass(
            templateOptions,
            "fa-percent"
          )
        ? [(record) => this.percentFilter(record)]
        : MachinesEditFormController.hasAddOnClass(templateOptions, "fa-usd")
        ? [(record) => this.currencyFilter(record, "$", 2)]
        : [];

    const fieldConfig = fieldHelpers.simpleReadonlyField({
      key: field.key!.toString(),
      className: field.className,
      label: field.templateOptions?.label,
      formatters,
    });

    return fieldConfig;
  }

  private getGameConfigReadonlyFieldGroup(
    fieldGroup: IFieldGroup
  ): IFieldGroup {
    return {
      ...fieldGroup,
      fieldGroup: fieldGroup.fieldGroup?.map((fg) =>
        this.getGameConfigReadonlyItem(fg)
      ),
    };
  }

  private getDropdownAddAddon(
    lookupName: string,
    valueSetter: (value: any, scope: IScope) => void,
    lookupModelFactory?: (
      options: ITemplateOptions,
      model: any,
      scope: IScope
    ) => IPromise<any>
  ) {
    return this.lookupModalService.getDropdownAddAddon(
      this.machineLookupManagementService,
      lookupName,
      valueSetter,
      "machineLookupModify",
      lookupModelFactory
    );
  }
}

class ProgramNumberFieldController {
  static $inject = ["$scope"];

  private readonly recordRepeaterController: RecordRepeaterController;

  constructor($scope: ITemplateScope) {
    const anyScope = <any>$scope;
    this.recordRepeaterController = anyScope.formState.getRepeaterController();
    anyScope.getProgramNumber = this.getProgramNumber.bind(this);
  }

  getProgramNumber(model: any) {
    const index = this.recordRepeaterController.getRowIndex(model);
    let programNumber = `Program ${index}`;
    switch (index) {
      case 0:
        programNumber = "Operating System";
        break;
      case 1:
        programNumber = "BV Program";
        break;
      case 2:
        programNumber = "Printer Firmware";
        break;
    }

    return programNumber;
  }
}
